import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { ArrowRight, Calendar, Edit3, MapPin, Star, Crown } from "lucide-react";
import { Button } from "./ui/button";

export const ListingCard = ({
  id,
  title,
  description,
  imageUrl,
  location,
  distanceBand,
  date,
  category,
  userName,
  userAvatar,
  userRating = 0,
  isOwner = false,
  currentUserId = null,
  ownerId = null,
  status = "active",
  isFoundingMember = false,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const canEdit =
    isOwner || (currentUserId && ownerId && currentUserId === ownerId);
  const isInactive = status === "inactive";

  const truncatedUserName =
    userName.length > 15 ? userName.slice(0, 15) + "..." : userName;

  /**
   * Get a display-friendly location string.
   * The new schema gives us a pre-built label, so this is much simpler.
   * Falls back to building from addressParts if label is missing,
   * or to "Location not available" if neither is present.
   */
  const getLocationString = () => {
    if (!location) return "Location not specified";

    if (location.label) {
      return location.label;
    }

    // Fallback: try to build from addressParts (e.g. if reverse-geocode failed
    // but we still have some structured data)
    if (location.addressParts) {
      const { suburb, city, country } = location.addressParts;
      const parts = [suburb, city, country].filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
    }

    return "Location not available";
  };

  const ImageWrapper = ({ children }) => {
    if (isInactive) {
      return (
        <div className="block relative aspect-[4/3] overflow-hidden cursor-not-allowed">
          {children}
        </div>
      );
    }
    return (
      <Link
        to={`/listing/${id}`}
        className="block relative aspect-[4/3] overflow-hidden"
      >
        {children}
      </Link>
    );
  };

  return (
    <div
      className={`rounded-xl overflow-hidden bg-white border border-border/60 shadow-sm transition-all duration-300 group card-hover ${
        isInactive ? "opacity-60 grayscale-[50%]" : "hover:shadow-md"
      }`}
    >
      <ImageWrapper>
        <div
          className={`absolute inset-0 bg-gray-200 ${
            imageLoaded ? "opacity-0" : "opacity-100"
          } transition-opacity duration-300 image-loading`}
        ></div>
        <img
          src={imageUrl}
          alt={title}
          className={`w-full h-full object-cover transition-all duration-500 ${
            imageLoaded ? "opacity-100 scale-100" : "opacity-0 scale-105"
          } ${!isInactive ? "group-hover:scale-105" : ""}`}
          onLoad={() => setImageLoaded(true)}
        />
        <Badge className="absolute top-3 left-3 backdrop-blur-sm">
          {category?.name}
        </Badge>
        {isInactive && (
          <Badge
            variant="secondary"
            className="absolute top-3 right-3 backdrop-blur-sm bg-gray-500/80 text-white"
          >
            Inactive
          </Badge>
        )}
      </ImageWrapper>

      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3
              className={`font-semi-bold text-lg mb-1 line-clamp-1 ${
                isInactive ? "text-gray-500" : ""
              }`}
            >
              {title}
            </h3>
            <div className="flex items-center space-x-3 text-sm text-muted-foreground flex-wrap gap-y-1">
              <div className="flex items-center min-w-0">
                <MapPin className="h-3.5 w-3.5 mr-1 text-igbanwo-mint flex-shrink-0" />
                <span className="truncate">{getLocationString()}</span>
                {distanceBand && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-igbanwo-mint/10 text-igbanwo-mint text-xs font-medium flex-shrink-0">
                    {distanceBand}
                  </span>
                )}
              </div>
              <div className="flex items-center flex-shrink-0">
                <Calendar className="h-3.5 w-3.5 mr-1 text-igbanwo-mint" />
                <span>{date}</span>
              </div>
            </div>
          </div>
        </div>

        <p
          className={`mb-4 text-sm ${
            isInactive ? "text-gray-400" : "text-muted-foreground"
          }`}
        >
          {description.length > 125
            ? description.slice(0, 125) + "..."
            : description}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-border/60">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-igbanwo-mint/10 flex items-center justify-center overflow-hidden">
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt={userName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{userName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" title={userName}>
                  {truncatedUserName}
                </span>
                {isFoundingMember && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs px-1.5 py-0.5 gap-1">
                    <Crown className="h-2.5 w-2.5" />
                  </Badge>
                )}
              </div>
              {userRating > 0 && (
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-3 w-3 ${
                        star <= Math.round(userRating)
                          ? "text-harvest-500 fill-harvest-500"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Link to={`/listing/${id}/edit`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </Button>
              </Link>
            )}
            {!isInactive && (
              <Link to={`/listing/${id}`}>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 group"
                >
                  Details
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};